import { Requests } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { AnalyticsCustomersSurvey } from '../../domain/analytics/AnalyticsCustomersSurvey';
import { AnalyticsDriversSurvey } from '../../domain/analytics/AnalyticsDriversSurvey';
import { DateChartAnalytics } from '../../domain/analytics/DateChartAnalytics';
import { HourChartAnalytics } from '../../domain/analytics/HourChartAnalytics';
import { StopStatus } from '../../domain/stop/StopStatus';
import { BinTypes, DateChartDataDTO, HourChartDataDTO } from '../../interfaces/analytics';
import { SessionRepository } from '../session/SessionRepository';
import { AnalyticsRepository } from './AnalyticsRepository';

export class AnalyticsRepositoryApi implements AnalyticsRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}
  async getAnalyticsPlans({
    startDate,
    endDate,
    planStatus,
    binType,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    planStatus?: string;
    binType: BinTypes;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/planner/plans`,
      params: {
        start_date: startDate,
        end_date: endDate,
        plan_status: planStatus,
        bin_type: binType,
        project_id: projectId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsSentSMS({
    startDate,
    endDate,
    status,
    binType,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    status: string;
    binType: BinTypes;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/customers/sms`,
      params: {
        start_date: startDate,
        end_date: endDate,
        status: status,
        bin_type: binType,
        project_id: projectId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsSentEmails({
    startDate,
    endDate,
    status,
    binType,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    status: string;
    binType: BinTypes;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/customers/emails`,
      params: {
        start_date: startDate,
        end_date: endDate,
        status: status,
        bin_type: binType,
        project_id: projectId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsPOD({
    startDate,
    endDate,
    binType,
    vehicleId,
    sequence,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    sequence: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/planner/pods`,
      params: {
        start_date: startDate,
        end_date: endDate,
        bin_type: binType,
        vehicle_id: vehicleId,
        sequence,
        project_id: projectId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsCancellationReasons({
    startDate,
    endDate,
    binType,
    vehicleId,
    sequence,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    sequence: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/planner/cancel_reasons`,
      params: {
        start_date: startDate,
        end_date: endDate,
        bin_type: binType,
        vehicle_id: vehicleId,
        sequence,
        project_id: projectId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsStops({
    startDate,
    endDate,
    binType,
    stopStatus,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    stopStatus?: StopStatus;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/planner/stops`,
      params: {
        start_date: startDate,
        end_date: endDate,
        bin_type: binType,
        stop_status: stopStatus,
        vehicle_id: vehicleId,
        project_id: projectId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsDeliveryTime({
    startDate,
    endDate,
    vehicleId,
    reportType,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    vehicleId?: string;
    reportType: string;
    projectId: string;
  }): Promise<HourChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/planner/delivery_time`,
      params: {
        start_date: startDate,
        end_date: endDate,
        vehicle_id: vehicleId,
        report_type: reportType,
        project_id: projectId,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    if (response.status !== 200) {
      return [];
    }

    const data = response.data.map((chartData: HourChartDataDTO) =>
      HourChartAnalytics.create({
        value: chartData.value,
        hour: chartData.hour,
      })
    );

    data.sort((a: HourChartAnalytics, b: HourChartAnalytics) => (a.hour < b.hour ? -1 : 1));

    return data;
  }
  async getAnalyticsTimeOnRoute({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/drivers/route_time`,
      params: {
        start_date: startDate,
        end_date: endDate,
        vehicle_id: vehicleId,
        project_id: projectId,
        bin_type: binType,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    if (response.status !== 200) {
      return [];
    }

    return response.data.map((chartData: DateChartDataDTO) =>
      DateChartAnalytics.create({
        date: chartData.date,
        value: chartData.value / 60,
      })
    );
  }
  async getAnalyticsAverageDelays({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/drivers/avg_delay`,
      params: {
        start_date: startDate,
        end_date: endDate,
        vehicle_id: vehicleId,
        project_id: projectId,
        bin_type: binType,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsRouteTracking({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/drivers/route_tracking`,
      params: {
        start_date: startDate,
        end_date: endDate,
        vehicle_id: vehicleId,
        project_id: projectId,
        bin_type: binType,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    return response.data.map((chartData: DateChartDataDTO) =>
      DateChartAnalytics.create({
        date: chartData.date,
        value: chartData.value * 100 !== 0 ? +(chartData.value * 100).toFixed(2) : chartData.value * 100,
      })
    );
  }
  async getAnalyticsDeliveryAttempts({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/drivers/delivery_attempts`,
      params: {
        start_date: startDate,
        end_date: endDate,
        vehicle_id: vehicleId,
        project_id: projectId,
        bin_type: binType,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsDriversSatisfaction({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/drivers/satisfaction`,
      params: {
        start_date: startDate,
        end_date: endDate,
        vehicle_id: vehicleId,
        project_id: projectId,
        bin_type: binType,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsDriversSurveys({
    startDate,
    endDate,
    projectId,
    vehicleIds = [],
    offset = 0,
    limit = 25,
    sort,
  }: {
    startDate: Date;
    endDate: Date;
    projectId: string;
    vehicleIds?: string[] | undefined;
    offset?: number | undefined;
    limit?: number | undefined;
    sort?: number | undefined;
  }): Promise<PaginatedResult<AnalyticsDriversSurvey>> {
    const response = await this.requests.post({
      uri: `/analytics/drivers/surveys`,
      data: {
        start_date: startDate,
        end_date: endDate,
        vehicle_ids: vehicleIds,
        project_id: projectId,
        sort,
        limit,
        offset,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    if (response.status !== 200) {
      return {
        docs: [],
        total: 0,
        offset,
        limit,
        page: 1,
        pages: 1,
      };
    }

    return {
      ...response.data,
      docs: response.data.docs.map(AnalyticsDriversSurvey.create),
    };
  }
  async getAnalyticsCustomersSatisfaction({
    startDate,
    endDate,
    binType,
    vehicleId,
    projectId,
  }: {
    startDate: Date;
    endDate: Date;
    binType: BinTypes;
    vehicleId?: string;
    projectId: string;
  }): Promise<DateChartAnalytics[]> {
    const response = await this.requests.get({
      uri: `/analytics/customers/satisfaction`,
      params: {
        start_date: startDate,
        end_date: endDate,
        vehicle_id: vehicleId,
        project_id: projectId,
        bin_type: binType,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    const data = response.data as DateChartDataDTO[];

    return data.map(DateChartAnalytics.create);
  }
  async getAnalyticsCustomersSurveys({
    startDate,
    endDate,
    projectId,
    vehicleIds = [],
    offset = 0,
    limit = 25,
    sort,
  }: {
    startDate: Date;
    endDate: Date;
    projectId?: string;
    vehicleIds?: string[] | undefined;
    offset?: number | undefined;
    limit?: number | undefined;
    sort?: number | undefined;
  }): Promise<PaginatedResult<AnalyticsCustomersSurvey>> {
    const response = await this.requests.post({
      uri: `/analytics/customers/surveys`,
      data: {
        start_date: startDate,
        end_date: endDate,
        vehicle_ids: vehicleIds,
        project_id: projectId,
        sort,
        limit,
        offset,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    if (response.status !== 200) {
      return {
        docs: [],
        total: 0,
        offset,
        limit,
        page: 1,
        pages: 1,
      };
    }
    return {
      ...response.data,
      docs: response.data.docs.map(AnalyticsCustomersSurvey.create),
    };
  }
}
